// Zustand store pentru gestionarea categoriilor personalizate (fuziune cu predefinite, acțiuni CRUD)
// Owner: echipa FE
import { create } from "zustand";
import { persist, devtools } from "zustand/middleware";
import { use } from "react";
import { CustomCategory, CustomSubcategory } from "../types/Category";
import { categoryService } from "../services/categoryService";
import { supabaseService } from "../services/supabaseService";
import {
  BaseStoreState,
  storeLogger,
  createDevtoolsOptions,
  createAsyncAction,
  createPersistConfig,
} from "./storeUtils";

// 🔄 React 19 use() hook imports for enhanced category patterns
import { useConditionalPromise, preloadPromise } from "../hooks/usePromise";

interface CategoryStoreState extends BaseStoreState {
  categories: CustomCategory[];
  version: number;

  // Async actions
  loadUserCategories: (userId: string) => Promise<void | null>;
  saveCategories: (
    userId: string,
    categories: CustomCategory[],
  ) => Promise<void | null>;
  renameSubcategory: (
    userId: string,
    category: string,
    oldName: string,
    newName: string,
  ) => Promise<boolean | null>;
  deleteSubcategory: (
    userId: string,
    category: string,
    subcategory: string,
    action: "migrate" | "delete",
    target?: string,
  ) => Promise<boolean | null>;

  // Sync actions
  mergeWithDefaults: (defaults: CustomCategory[]) => void;
  getSubcategoryCount: (category: string, subcategory: string) => number;
  setCategories: (categories: CustomCategory[]) => void;
  incrementVersion: () => void;
}

const STORE_NAME = "CategoryStore";

export const useCategoryStore = create<CategoryStoreState>()(
  devtools(
    persist(
      (set, get) => {
        // Helper pentru logging standardizat
        const logAction = (action: string, data?: Record<string, unknown>) => {
          storeLogger.info(STORE_NAME, action, data);
        };

        // Helper pentru setări standardizate
        const setLoading = (loading: boolean) => {
          set({ loading, lastUpdated: new Date() }, false, "setLoading");
        };

        const setError = (error: string | null) => {
          set({ error, lastUpdated: new Date() }, false, "setError");
          if (error) {
            storeLogger.error(STORE_NAME, "Error set", error);
          }
        };

        // Creăm acțiuni async standardizate
        const createCategoryAction = <T extends unknown[], R = void>(
          actionName: string,
          action: (...args: T) => Promise<R>,
        ) =>
          createAsyncAction(
            STORE_NAME,
            actionName,
            action,
            setLoading,
            setError,
          );

        return {
          // State inițial cu BaseStoreState
          loading: false,
          error: null,
          lastUpdated: new Date(),

          // Category state
          categories: [],
          version: 1,

          // Async actions cu error handling standardizat
          loadUserCategories: createCategoryAction(
            "loadUserCategories",
            async (userId: string) => {
              if (!userId) {
                const errorMsg = "User ID is required for loading categories";
                storeLogger.warn(STORE_NAME, errorMsg);
                throw new Error(errorMsg);
              }

              const data = await categoryService.getUserCategories(userId);

              // Validare - ne asigurăm că data.categories este întotdeauna un array
              if (data && Array.isArray(data.categories)) {
                set(
                  {
                    categories: data.categories,
                    version: data.version || 1,
                    error: null,
                    lastUpdated: new Date(),
                  },
                  false,
                  "loadUserCategories_success",
                );

                logAction("Categories loaded successfully", {
                  count: data.categories.length,
                  version: data.version,
                });
              } else {
                // Initializăm cu array gol dacă nu avem date valide
                set(
                  {
                    categories: [],
                    version: 1,
                    error: null,
                    lastUpdated: new Date(),
                  },
                  false,
                  "loadUserCategories_empty",
                );

                logAction("No categories found, starting with empty list");
              }
            },
          ),

          saveCategories: createCategoryAction(
            "saveCategories",
            async (userId: string, categories: CustomCategory[]) => {
              const newVersion = get().version + 1;
              
              // 🚀 OPTIMISTIC UPDATE: Update UI immediately
              set(
                {
                  categories,
                  version: newVersion,
                  error: null,
                  lastUpdated: new Date(),
                },
                false,
                "saveCategories_optimistic",
              );

              logAction("Categories saved optimistically", {
                count: categories.length,
                version: newVersion,
              });

              try {
                const success = await categoryService.saveUserCategories(userId, {
                  categories,
                  version: newVersion,
                });

                if (success) {
                  logAction("Categories saved successfully in backend", {
                    count: categories.length,
                    version: newVersion,
                  });
                } else {
                  throw new Error("Failed to save categories to backend");
                }
              } catch (error) {
                // 🔄 ROLLBACK: If backend fails, revert the optimistic update
                set(
                  {
                    categories: get().categories,
                    version: get().version - 1,
                    lastUpdated: new Date(),
                  },
                  false,
                  "saveCategories_rollback",
                );

                logAction("Categories save rolled back due to error", {
                  error: error instanceof Error ? error.message : String(error),
                });

                throw error;
              }
            },
          ),

          renameSubcategory: createCategoryAction(
            "renameSubcategory",
            async (
              userId: string,
              category: string,
              oldName: string,
              newName: string,
            ) => {
              // 🚀 OPTIMISTIC UPDATE: Update UI immediately
              const updatedCategories = get().categories.map(
                (cat: CustomCategory) =>
                  cat.name === category
                    ? {
                        ...cat,
                        subcategories: cat.subcategories.map((sc) =>
                          sc.name === oldName ? { ...sc, name: newName } : sc,
                        ),
                      }
                    : cat,
              );

              const newVersion = get().version + 1;

              // Update store FIRST for optimistic UI
              set(
                {
                  categories: updatedCategories,
                  version: newVersion,
                  lastUpdated: new Date(),
                },
                false,
                "renameSubcategory_optimistic",
              );

              logAction("Subcategory renamed optimistically", {
                category,
                oldName,
                newName,
              });

              try {
                // Save to backend
                await categoryService.saveUserCategories(userId, {
                  categories: updatedCategories,
                  version: newVersion,
                });

                // Update transactions
                await categoryService.updateTransactionsForSubcategoryRename(
                  userId,
                  category,
                  oldName,
                  newName,
                );

                logAction("Subcategory renamed successfully in backend", {
                  category,
                  oldName,
                  newName,
                });

                return true;
              } catch (error) {
                // 🔄 ROLLBACK: If backend fails, revert the optimistic update
                const originalCategories = get().categories.map(
                  (cat: CustomCategory) =>
                    cat.name === category
                      ? {
                          ...cat,
                          subcategories: cat.subcategories.map((sc) =>
                            sc.name === newName ? { ...sc, name: oldName } : sc,
                          ),
                        }
                      : cat,
                );

                set(
                  {
                    categories: originalCategories,
                    version: get().version - 1,
                    lastUpdated: new Date(),
                  },
                  false,
                  "renameSubcategory_rollback",
                );

                logAction("Subcategory rename rolled back due to error", {
                  category,
                  oldName,
                  newName,
                  error: error instanceof Error ? error.message : String(error),
                });

                throw error;
              }
            },
          ),

          deleteSubcategory: createCategoryAction(
            "deleteSubcategory",
            async (
              userId: string,
              category: string,
              subcategory: string,
              action: "migrate" | "delete",
              target?: string,
            ) => {
              // Store original state for rollback
              const originalCategories = get().categories;
              
              // 🚀 OPTIMISTIC UPDATE: Update UI immediately
              const updatedCategories = get().categories.map(
                (cat: CustomCategory) =>
                  cat.name === category
                    ? {
                        ...cat,
                        subcategories: cat.subcategories.filter(
                          (sc) => sc.name !== subcategory,
                        ),
                      }
                    : cat,
              );

              const newVersion = get().version + 1;

              // Update store FIRST for optimistic UI
              set(
                {
                  categories: updatedCategories,
                  version: newVersion,
                  lastUpdated: new Date(),
                },
                false,
                "deleteSubcategory_optimistic",
              );

              logAction("Subcategory deleted optimistically", {
                category,
                subcategory,
                action,
                target,
              });

              try {
                // Save to backend
                await categoryService.saveUserCategories(userId, {
                  categories: updatedCategories,
                  version: newVersion,
                });

                // Handle transaction migration/deletion
                await categoryService.handleSubcategoryDeletion(
                  userId,
                  category,
                  subcategory,
                  action,
                  target,
                );

                logAction("Subcategory deleted successfully in backend", {
                  category,
                  subcategory,
                  action,
                  target,
                });

                return true;
              } catch (error) {
                // 🔄 ROLLBACK: If backend fails, revert the optimistic update
                set(
                  {
                    categories: originalCategories,
                    version: get().version - 1,
                    lastUpdated: new Date(),
                  },
                  false,
                  "deleteSubcategory_rollback",
                );

                logAction("Subcategory delete rolled back due to error", {
                  category,
                  subcategory,
                  action,
                  target,
                  error: error instanceof Error ? error.message : String(error),
                });

                throw error;
              }
            },
          ),

          // Sync actions
          mergeWithDefaults: (defaults: CustomCategory[]) => {
            // Fuzionează categoriile predefinite cu cele custom, prioritate pentru custom
            const custom = get().categories;
            const merged = defaults.map((def: CustomCategory) => {
              const found = custom.find(
                (cat: CustomCategory) => cat.name === def.name,
              );
              if (found) {
                // Prioritate subcategorii custom
                const customSubs = found.subcategories.map(
                  (sc: CustomSubcategory) => sc.name,
                );
                return {
                  ...def,
                  subcategories: [
                    ...found.subcategories,
                    ...def.subcategories.filter(
                      (sc: CustomSubcategory) => !customSubs.includes(sc.name),
                    ),
                  ],
                  isCustom: found.isCustom,
                };
              }
              return def;
            });

            // Adaugă categoriile complet custom
            const onlyCustom = custom.filter(
              (cat: CustomCategory) =>
                !defaults.some((def: CustomCategory) => def.name === cat.name),
            );
            const finalCategories = [...merged, ...onlyCustom];

            set(
              {
                categories: finalCategories,
                lastUpdated: new Date(),
              },
              false,
              "mergeWithDefaults",
            );

            logAction("Categories merged with defaults", {
              defaultsCount: defaults.length,
              customCount: custom.length,
              mergedCount: finalCategories.length,
            });
          },

          getSubcategoryCount: (category: string, subcategory: string) => {
            // NOTĂ: Această metodă a fost actualizată pentru a elimina dependența de useTransactionStore.transactions
            // care a fost deprecat în favoarea React Query.
            //
            // Acum, aceasta este o metodă temporară care va returna mereu 0. Pentru o implementare
            // completă, s-ar putea face un apel direct la baza de date prin supabaseService sau
            // s-ar putea folosi React Query / useTransactions pentru a obține count-ul real.
            //
            // Recomandare: Dacă aveți nevoie de numărătoare exactă, implementați un endpoint dedicat
            // pentru counting în backend sau folosiți un hook dedicat React Query pentru statistici.

            logAction("getSubcategoryCount called (deprecated method)", {
              category,
              subcategory,
            });
            return 0; // Valoare temporară până la implementarea completă
          },

          setCategories: (categories: CustomCategory[]) => {
            set(
              {
                categories,
                lastUpdated: new Date(),
              },
              false,
              "setCategories",
            );
            logAction("Categories set manually", { count: categories.length });
          },

          incrementVersion: () => {
            const newVersion = get().version + 1;
            set(
              {
                version: newVersion,
                lastUpdated: new Date(),
              },
              false,
              "incrementVersion",
            );
            logAction("Version incremented", { version: newVersion });
          },

          // Base store actions
          setLoading,
          setError,
          clearError: () => {
            set({ error: null, lastUpdated: new Date() }, false, "clearError");
            logAction("Error cleared");
          },
          reset: () => {
            set(
              {
                loading: false,
                error: null,
                categories: [],
                version: 1,
                lastUpdated: new Date(),
              },
              false,
              "reset",
            );
            logAction("Store reset");
          },
        };
      },
      createPersistConfig("category", (state: CategoryStoreState) => ({
        categories: state.categories,
        version: state.version,
      })),
    ),
    createDevtoolsOptions(STORE_NAME),
  ),
);

// 🧪 E2E Testing Support: Expose store globally for testing
// Similar to development mode, but specifically for E2E tests
if (typeof window !== 'undefined') {
  // Check if we're in a testing environment
  const isE2E = window.location.hostname === 'localhost' && 
               (window.navigator.userAgent.includes('HeadlessChrome') || 
                window.navigator.webdriver === true ||
                (window as Window & { __playwright?: unknown }).__playwright !== undefined);
  
  if (isE2E || process.env.NODE_ENV === 'development') {
    (window as Window & { useCategoryStore?: typeof useCategoryStore }).useCategoryStore = useCategoryStore;
    console.log('🧪 CategoryStore exposed globally for E2E testing');
  }
}

// Selectori optimizați pentru performance
export const useCategoriesData = () =>
  useCategoryStore((state) => ({
    categories: state.categories,
    version: state.version,
    loading: state.loading,
    error: state.error,
  }));

export const useCategoryActions = () =>
  useCategoryStore((state) => ({
    loadUserCategories: state.loadUserCategories,
    saveCategories: state.saveCategories,
    renameSubcategory: state.renameSubcategory,
    deleteSubcategory: state.deleteSubcategory,
    mergeWithDefaults: state.mergeWithDefaults,
  }));

// =============================================================================
// 🚀 REACT 19 USE() HOOK ENHANCED CATEGORY PATTERNS
// =============================================================================

export interface CategoryV2Options {
  enablePreloading?: boolean;
  enableOptimisticUpdates?: boolean;
  enableAsyncValidation?: boolean;
  enableCaching?: boolean;
}

/**
 * React 19 enhanced category hooks with use() hook optimizations
 * 
 * Benefits:
 * - Preloaded category service for faster operations
 * - Promise-based category operations with use() hook integration
 * - Better Suspense integration for category flows
 * - Enhanced validation and caching patterns
 * - Optimistic updates with automatic rollback
 */

// Enhanced category data hook with preloading
export function useCategoriesDataV2(options: CategoryV2Options = {}) {
  const { enablePreloading = true, enableCaching = true } = options;
  
  const categoriesData = useCategoriesData();
  
  // 🔄 React 19: Preload category service for faster operations
  React.useEffect(() => {
    if (enablePreloading) {
      preloadPromise(
        import("../services/categoryService"),
        'category-service',
        'CategoryServicePreload'
      );
    }
  }, [enablePreloading]);
  
  return categoriesData;
}

// Enhanced category operations with use() hook patterns
export function useCategoryOperationsV2(options: CategoryV2Options = {}) {
  const { enablePreloading = true, enableOptimisticUpdates = true } = options;
  const actions = useCategoryActions();
  
  // 🔄 React 19: Enhanced category operations with preloaded service
  // React Compiler optimizes - no useMemo needed
  const enhancedOperations = {
    async loadUserCategories(userId: string) {
      console.log('🚀 [React 19 Category] Starting enhanced load');
      
      try {
        // Preload category service if needed
        if (enablePreloading) {
          await import("../services/categoryService");
        }
        
        const result = await actions.loadUserCategories(userId);
        
        console.log('✅ [React 19 Category] Enhanced load successful');
        return result;
        
      } catch (error) {
        console.error('🚨 [React 19 Category] Enhanced load failed:', error);
        throw error;
      }
    },

    async saveCategories(userId: string, categories: CustomCategory[]) {
      console.log('🚀 [React 19 Category] Starting enhanced save');
      
      try {
        // Preload service if needed
        if (enablePreloading) {
          await import("../services/categoryService");
        }
        
        const result = await actions.saveCategories(userId, categories);
        
        console.log('✅ [React 19 Category] Enhanced save successful');
        return result;
        
      } catch (error) {
        console.error('🚨 [React 19 Category] Enhanced save failed:', error);
        throw error;
      }
    },

    async renameSubcategory(userId: string, category: string, oldName: string, newName: string) {
      console.log('🚀 [React 19 Category] Starting enhanced rename');
      
      try {
        if (enablePreloading) {
          await import("../services/categoryService");
        }
        
        const result = await actions.renameSubcategory(userId, category, oldName, newName);
        
        console.log('✅ [React 19 Category] Enhanced rename successful');
        return result;
        
      } catch (error) {
        console.error('🚨 [React 19 Category] Enhanced rename failed:', error);
        throw error;
      }
    },

    async deleteSubcategory(userId: string, category: string, subcategory: string, action: "migrate" | "delete", target?: string) {
      console.log('🚀 [React 19 Category] Starting enhanced delete');
      
      try {
        if (enablePreloading) {
          await import("../services/categoryService");
        }
        
        const result = await actions.deleteSubcategory(userId, category, subcategory, action, target);
        
        console.log('✅ [React 19 Category] Enhanced delete successful');
        return result;
        
      } catch (error) {
        console.error('🚨 [React 19 Category] Enhanced delete failed:', error);
        throw error;
      }
    }
  };
  
  return enhancedOperations;
}

// Enhanced category validation with use() hook
export function useCategoryValidationV2(options: CategoryV2Options = {}) {
  const { enableAsyncValidation = true } = options;
  const { categories, loading, error } = useCategoriesData();
  
  // 🔄 React 19: Conditional async validation with use() hook
  // React Compiler optimizes - no useMemo needed
  const validationPromise = (() => {
    if (!enableAsyncValidation || categories.length === 0) return null;
    
    return categoryService.validateCategoriesIntegrity(categories).then(validation => ({
      isValid: validation.isValid,
      issues: validation.issues || [],
      statistics: {
        totalCategories: categories.length,
        totalSubcategories: categories.reduce((sum, cat) => sum + cat.subcategories.length, 0),
        customCategories: categories.filter(cat => cat.isCustom).length,
        timestamp: Date.now()
      }
    }));
  })();
  
  const validation = useConditionalPromise(
    enableAsyncValidation && categories.length > 0 && !!validationPromise,
    validationPromise!,
    { 
      isValid: true, 
      issues: [], 
      statistics: {
        totalCategories: categories.length,
        totalSubcategories: 0,
        customCategories: 0,
        timestamp: Date.now()
      }
    },
    {
      cacheKey: `category-validation-${categories.length}-${JSON.stringify(categories).substring(0, 50)}`,
      retry: { attempts: 1, delay: 500 },
      name: 'CategoryValidation'
    }
  );
  
  return {
    categories,
    loading,
    error,
    validation: validation || { 
      isValid: true, 
      issues: [], 
      statistics: {
        totalCategories: categories.length,
        totalSubcategories: 0,
        customCategories: 0,
        timestamp: Date.now()
      }
    }
  };
}

// Enhanced category search with use() hook patterns
export function useCategorySearchV2(searchTerm: string, options: CategoryV2Options = {}) {
  const { enableCaching = true } = options;
  const { categories } = useCategoriesData();
  
  // 🔄 React 19: Promise-based search that can work with Suspense
  // React Compiler optimizes - no useMemo needed
  const searchPromise = (() => {
    if (!searchTerm.trim()) {
      return Promise.resolve(categories);
    }
    
    return new Promise<CustomCategory[]>((resolve) => {
      // Simulate async search for consistency with promise patterns
      setTimeout(() => {
        const filteredCategories = categories.filter(category => {
          const nameMatch = category.name.toLowerCase().includes(searchTerm.toLowerCase());
          const subcategoryMatch = category.subcategories.some(sub => 
            sub.name.toLowerCase().includes(searchTerm.toLowerCase())
          );
          return nameMatch || subcategoryMatch;
        });
        resolve(filteredCategories);
      }, 10);
    });
  })();
  
  const searchResults = useConditionalPromise(
    !!searchTerm.trim(),
    searchPromise,
    categories,
    {
      cacheKey: enableCaching ? `category-search-${searchTerm}-${categories.length}` : undefined,
      name: 'CategorySearch'
    }
  );
  
  return {
    searchResults,
    searchTerm,
    hasResults: searchResults.length > 0,
    resultCount: searchResults.length
  };
}

// Enhanced category statistics with React 19 patterns
export function useCategoryStatisticsV2(options: CategoryV2Options = {}) {
  const { enableAsyncValidation = true } = options;
  const { categories } = useCategoriesData();
  
  // 🔄 React 19: Enhanced statistics with async calculation
  // React Compiler optimizes - no useMemo needed
  const statisticsPromise = (() => {
    if (!enableAsyncValidation || categories.length === 0) return null;
    
    return new Promise(resolve => {
      // Simulate async calculation for complex statistics
      setTimeout(() => {
        const stats = {
          totalCategories: categories.length,
          totalSubcategories: categories.reduce((sum, cat) => sum + cat.subcategories.length, 0),
          customCategories: categories.filter(cat => cat.isCustom).length,
          defaultCategories: categories.filter(cat => !cat.isCustom).length,
          averageSubcategoriesPerCategory: categories.length > 0 
            ? categories.reduce((sum, cat) => sum + cat.subcategories.length, 0) / categories.length 
            : 0,
          categoriesWithMostSubcategories: categories
            .sort((a, b) => b.subcategories.length - a.subcategories.length)
            .slice(0, 3)
            .map(cat => ({ name: cat.name, count: cat.subcategories.length })),
          timestamp: Date.now()
        };
        resolve(stats);
      }, 50);
    });
  })();
  
  const statistics = useConditionalPromise(
    enableAsyncValidation && categories.length > 0 && !!statisticsPromise,
    statisticsPromise!,
    {
      totalCategories: categories.length,
      totalSubcategories: 0,
      customCategories: 0,
      defaultCategories: 0,
      averageSubcategoriesPerCategory: 0,
      categoriesWithMostSubcategories: [],
      timestamp: Date.now()
    },
    {
      cacheKey: `category-statistics-${categories.length}`,
      retry: { attempts: 1, delay: 300 },
      name: 'CategoryStatistics'
    }
  );
  
  return statistics;
}

// Preload category dependencies for better performance
export function useCategoryPreloadV2() {
  React.useEffect(() => {
    // Preload category service
    preloadPromise(
      import("../services/categoryService"),
      'category-service-global',
      'GlobalCategoryServicePreload'
    );
    
    // Preload validation utilities
    // TODO: Add categoryValidation utils when needed
    // preloadPromise(
    //   import("../utils/categoryValidation"),
    //   'category-validation-utils',
    //   'CategoryValidationUtilsPreload'
    // );
    
    console.log('🚀 [React 19 Category] Category dependencies preloaded');
  }, []);
}

// Enhanced category guard hook with React 19 patterns
export function useCategoryGuardV2(requiredCategoryCount: number = 1, options: CategoryV2Options = {}) {
  const { enableAsyncValidation = true } = options;
  const { categories, loading, error } = useCategoriesData();
  
  // 🔄 React 19: Promise-based guard that can work with Suspense
  // React Compiler optimizes - no useMemo needed
  const guardPromise = (() => {
    if (categories.length >= requiredCategoryCount) {
      return Promise.resolve(true);
    } else if (loading) {
      return new Promise(resolve => {
        const checkCategories = () => {
          const currentState = useCategoryStore.getState();
          if (!currentState.loading) {
            resolve(currentState.categories.length >= requiredCategoryCount);
          } else {
            setTimeout(checkCategories, 100);
          }
        };
        checkCategories();
      });
    } else {
      return Promise.resolve(false);
    }
  })();
  
  return {
    hasRequiredCategories: categories.length >= requiredCategoryCount,
    loading,
    error,
    guardPromise,
    categoryCount: categories.length,
    requiredCount: requiredCategoryCount
  };
}
